import { PlusIcon } from "@phosphor-icons/react/Plus";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { ControlPresentationProvider } from "@freightsimple/ui-core/Controls/ControlPresentationProvider";
import { Dialog } from "@freightsimple/ui-core/Dialog/Dialog";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import type { Invitation } from "@freightsimple/generated-dashboard-openapi-client/models/Invitation";
import type { User } from "@freightsimple/generated-dashboard-openapi-client/models/User";
import { useState } from "react";
import FullWidthLayout from "@/Shared/Layout/CustomComponents/FullWidthLayout";
import { useOnce } from "@/Hooks/useOnce";
import { useUserService } from "@/Services/UserService";
import ViewCompanyInvitationsSection from "./CustomComponents/ViewCompanyInvitationsSection";
import ViewCompanyUsersSection from "./CustomComponents/ViewCompanyUsersSection";
import { ViewCompanyInviteDialog } from "./Dialogs/ViewCompanyInviteDialog";

function ViewCompanyScreen() {
  const [inviteOpen, setInviteOpen] = useState(false);
  const service = useUserService();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Revoke/resend dialogs live at screen level, not inside ViewCompanyInvitationsSection:
  // revoking the last invitation empties the list, which unmounts the section
  // — a dialog owned there would silently vanish before the user sees it.
  const [revokeTarget, setRevokeTarget] = useState<string | undefined>();
  const [notice, setNotice] = useState<
    { title: string; message: string } | undefined
  >();

  async function loadInvitations() {
    const _invitations = await service.listInvitationsForCompany();
    setInvitations(_invitations);
  }

  /* RC: July 29 – Don't use this function to **refresh** the user list.
   * Use the setter from the useState hook when handling Auth0 updates.
   * The Auth0 list function won't reflect updates immediately.
   * Auht0 documentation about the "list users" endpoint:
   * > This endpoint is eventually consistent, and as such,
   * > we recommend that you use this endpoint for back office
   * > processes such as changing the display name of an existing user.
   */
  async function fetchUsers() {
    const _users = await service.listUsersForCompany();
    setUsers(_users);
  }

  function onInvite() {
    setInviteOpen(true);
  }

  async function onRevokeConfirmed() {
    if (revokeTarget === undefined) {
      return;
    }
    setRevokeTarget(undefined);
    try {
      await service.revokeInvitation(revokeTarget);

      await loadInvitations();
      setNotice({ title: "Revoke", message: "The invitation was revoked" });
    } catch (e) {
      console.error("Something went wrong revoking", { e });
      setNotice({
        title: "Error",
        message: "Something went wrong. Please try again or ask us for help",
      });
    }
  }

  async function onResend(inviteeEmailAddress: string) {
    try {
      await service.resendInvitation(inviteeEmailAddress);

      await loadInvitations();
      setNotice({
        title: "Resent",
        message: "The invitation was successfully resent",
      });
    } catch (e) {
      console.error("Something went wrong resending", { e });
      setNotice({
        title: "Error",
        message: "Something went wrong. Please try again or ask us for help",
      });
    }
  }

  useOnce(async function () {
    setIsLoading(() => true);
    try {
      await Promise.all([loadInvitations(), fetchUsers()]);
    } catch (e) {
      console.error(e);
    }
    setIsLoading(() => false);
  });

  return (
    <>
      <ViewCompanyInviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInviteSent={() => loadInvitations()}
      />
      <Dialog
        open={revokeTarget !== undefined}
        onOpenChange={(open) => {
          if (!open) setRevokeTarget(undefined);
        }}
        title="Revoke"
        description="Are you sure you want to revoke the invitation?"
        okText="Revoke Invitation"
        onOk={onRevokeConfirmed}
      >
        <ControlPresentationProvider presentation="underlined">
          <></>
        </ControlPresentationProvider>
      </Dialog>
      <Dialog
        open={notice !== undefined}
        onOpenChange={(open) => {
          if (!open) setNotice(undefined);
        }}
        title={notice?.title}
        footer={
          <Button primary onClick={() => setNotice(undefined)}>
            OK
          </Button>
        }
      >
        <ControlPresentationProvider presentation="underlined">
          {notice?.message}
        </ControlPresentationProvider>
      </Dialog>
      <FullWidthLayout
        header="Manage Users"
        microcopy="Invite colleagues and view who has access to this account"
        rightContent={
          <Button
            primary
            icon={PlusIcon}
            onClick={onInvite}
            data-testid="invite-user"
          >
            Invite User
          </Button>
        }
        loading={isLoading}
        showEmptyState={users.length === 0 && invitations.length === 0}
        emptyState={
          <Stack gap={6} align="center">
            <Stack gap={2} align="center">
              <Heading level={1}>No other users have been added yet</Heading>
              <T color="secondary">
                Invite colleagues to manage shipments and control access to this
                account.
              </T>
            </Stack>
            <Button
              primary
              icon={PlusIcon}
              onClick={onInvite}
              data-testid="invite-user"
            >
              Send Invitation
            </Button>
          </Stack>
        }
        content={
          <Stack gap={8}>
            {invitations.length > 0 && (
              <ViewCompanyInvitationsSection
                invitations={invitations}
                onResend={onResend}
                onRevoke={setRevokeTarget}
              />
            )}
            {users.length > 0 && (
              <Stack gap={4}>
                <Heading level={2}>Existing Users</Heading>
                <ViewCompanyUsersSection users={users} setUsers={setUsers} />
              </Stack>
            )}
          </Stack>
        }
      />
    </>
  );
}

export default ViewCompanyScreen;
